import Image from 'next/image';
import Link from 'next/link';
import type { FeaturedEvent } from '@/hooks/use-featured-event';
import { EventReminderButton } from '@/components/event-reminder-button';

/**
 * Compact preview of the promoted event shown beside the category list in
 * the Kajian dropdown. The promoted event is the admin-highlighted one when
 * set, otherwise the nearest upcoming event. The "Ingatkan Saya" button
 * only makes sense for events that have not started yet, so it is hidden
 * for past highlighted events.
 */
export function KajianEventPreview({
  event,
  isHighlighted,
}: {
  event: FeaturedEvent;
  isHighlighted: boolean;
}) {
  const isUpcoming = new Date(event.startAt).getTime() > Date.now();

  return (
    <div className="w-[300px] shrink-0 p-1.5">
      <Link
        href={`/blog/event/${event.slug}`}
        className="group block overflow-hidden rounded-lg border border-neutral-200 transition-colors hover:border-neutral-300"
      >
        <div className="relative aspect-video overflow-hidden bg-neutral-100">
          {event.featuredImage ? (
            <Image
              src={event.featuredImage}
              alt={event.title}
              fill
              sizes="300px"
              className="object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500">
              <span className="text-xs font-bold uppercase tracking-widest text-white/50">
                Justisio
              </span>
            </div>
          )}
        </div>
        <div className="p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
            {isHighlighted ? 'Event Unggulan' : 'Event Mendatang'}
          </p>
          <h3 className="mt-1 text-sm font-semibold leading-snug text-neutral-900 line-clamp-2 group-hover:underline decoration-1 underline-offset-2">
            {event.title}
          </h3>
          {event.excerpt && (
            <p className="mt-1 text-xs leading-relaxed text-neutral-500 line-clamp-2">
              {event.excerpt}
            </p>
          )}
        </div>
      </Link>

      {isUpcoming && (
        <EventReminderButton
          slug={event.slug}
          className="mt-2"
          event={{
            title: event.title,
            startAt: event.startAt,
            endAt: event.endAt,
            timezone: event.timezone,
            description: event.excerpt,
          }}
        />
      )}
    </div>
  );
}
